import { CAD_PARCEL_STOCK_STYLE } from '../../Map/styles/mapTheme';
import { typography } from '../../styles/theme';
import { CAD_STOCK_STATUSES } from '../data/cadConstants';
import { buildCadLotMutedExpression, getCadStockColor } from './cadStockStatus';

export const CAD_LOT_CHIP_IMAGE_PREFIX = 'cad-lot-chip-sm-';

const CHIP_PIXEL_RATIO = 2;
const CHIP_HEIGHT = 18;
const CHIP_PAD_X = 6;
const CHIP_DOT = 6;
const CHIP_GAP = 4;
const CHIP_BLEED = 3;
const CHIP_FONT_SIZE = 11;
const CHIP_FONT = `${typography.fontWeight.semibold} ${CHIP_FONT_SIZE}px ${typography.fontFamily.primary}`;

const fontRefreshPending = new WeakSet();

/**
 * @param {string|null|undefined} label
 * @returns {string}
 */
export function sanitizeCadLotChipLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return '';
  return raw.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_|_$/g, '').slice(0, 24);
}

/**
 * @param {string|null|undefined} status
 * @param {string|null|undefined} [label]
 * @returns {string}
 */
export function cadLotChipImageId(status, label = '') {
  const id = CAD_STOCK_STATUSES.includes(status) ? status : 'available';
  const key = sanitizeCadLotChipLabel(label);
  return key
    ? `${CAD_LOT_CHIP_IMAGE_PREFIX}${id}-${key}`
    : `${CAD_LOT_CHIP_IMAGE_PREFIX}${id}`;
}

function drawCapsulePath(ctx, x, y, width, height) {
  const radius = height / 2;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, width, height, radius);
    return;
  }
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arc(x + width - radius, y + radius, radius, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + radius, y + height);
  ctx.arc(x + radius, y + radius, radius, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
}

/**
 * Pastilla tipo Airbnb: blanca, borde fino, sombra y Inter semibold.
 * El puntito de stock queda; el número no usa glifos Open Sans.
 *
 * @param {string} dotColor
 * @param {string} [label]
 * @param {string} [fillColor]
 * @returns {{ data: ImageData, pixelRatio: number }|null}
 */
export function createCadLotChipStyleImage(
  dotColor,
  label = '',
  fillColor = CAD_PARCEL_STOCK_STYLE.chipFill,
) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const text = String(label || '');
  ctx.font = CHIP_FONT;
  const textW = Math.ceil(ctx.measureText(text).width);
  const innerW = CHIP_PAD_X + CHIP_DOT + (text ? CHIP_GAP + textW : 0) + CHIP_PAD_X;
  const innerH = CHIP_HEIGHT;
  const width = innerW + CHIP_BLEED * 2;
  const height = innerH + CHIP_BLEED * 2;

  canvas.width = Math.ceil(width * CHIP_PIXEL_RATIO);
  canvas.height = Math.ceil(height * CHIP_PIXEL_RATIO);
  ctx.setTransform(CHIP_PIXEL_RATIO, 0, 0, CHIP_PIXEL_RATIO, 0, 0);
  ctx.font = CHIP_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.imageSmoothingEnabled = true;

  ctx.clearRect(0, 0, width, height);
  ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = fillColor;
  drawCapsulePath(ctx, CHIP_BLEED, CHIP_BLEED, innerW, innerH);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = CAD_PARCEL_STOCK_STYLE.chipBorder;
  ctx.lineWidth = 1;
  drawCapsulePath(ctx, CHIP_BLEED + 0.5, CHIP_BLEED + 0.5, innerW - 1, innerH - 1);
  ctx.stroke();

  const cy = CHIP_BLEED + innerH / 2;
  const dotR = CHIP_DOT / 2;
  ctx.beginPath();
  ctx.arc(CHIP_BLEED + CHIP_PAD_X + dotR, cy, dotR, 0, Math.PI * 2);
  ctx.fillStyle = dotColor;
  ctx.fill();

  if (text) {
    ctx.fillStyle = CAD_PARCEL_STOCK_STYLE.chipText;
    ctx.fillText(text, CHIP_BLEED + CHIP_PAD_X + CHIP_DOT + CHIP_GAP, cy);
  }

  return {
    data: ctx.getImageData(0, 0, canvas.width, canvas.height),
    pixelRatio: CHIP_PIXEL_RATIO,
  };
}

/**
 * @returns {import('maplibre-gl').ExpressionSpecification}
 */
export function buildCadLotChipImageExpression() {
  return ['coalesce', ['get', 'chip_image'], ''];
}

/**
 * Un poco más chicos al acercar, para que no tapen el lote.
 * @returns {import('maplibre-gl').ExpressionSpecification}
 */
export function cadLotChipIconSizeExpression() {
  return [
    'interpolate', ['linear'], ['zoom'],
    CAD_PARCEL_STOCK_STYLE.labelMinZoom, 1,
    20, 0.82,
  ];
}

/**
 * Fade-in corto al cruzar el umbral. Lotes muteados (filtro / stock) → 0.
 * El `interpolate` de zoom va arriba: MapLibre no admite `['zoom']` anidado
 * en un `case` (los chips quedaban invisibles).
 *
 * @param {string[]|null|undefined} [visibleIds]
 * @returns {import('maplibre-gl').ExpressionSpecification}
 */
export function cadLotChipIconOpacityExpression(visibleIds) {
  const start = CAD_PARCEL_STOCK_STYLE.labelMinZoom;
  const end = start + CAD_PARCEL_STOCK_STYLE.labelFadeZoom;
  const whenVisible = [
    'case',
    buildCadLotMutedExpression(visibleIds), 0,
    1,
  ];
  return [
    'interpolate', ['linear'], ['zoom'],
    start, 0,
    end, whenVisible,
  ];
}

function listCadLotChipKeys(features) {
  const seen = new Set();
  const keys = [];
  (Array.isArray(features) ? features : []).forEach((feature) => {
    const label = feature?.properties?.map_label;
    if (!label) return;
    const status = feature?.properties?.stock_status;
    const id = feature?.properties?.chip_image || cadLotChipImageId(status, label);
    if (seen.has(id)) return;
    seen.add(id);
    keys.push({ id, status, label: String(label) });
  });
  return keys;
}

function upsertCadLotChipImage(map, id, image, replace) {
  try {
    if (map.hasImage?.(id)) {
      if (replace && typeof map.updateImage === 'function') {
        map.updateImage(id, image.data);
      }
      return true;
    }
    map.addImage(id, image.data, { pixelRatio: image.pixelRatio });
    return true;
  } catch {
    return false;
  }
}

function paintCadLotChipImages(map, features, replace = false) {
  let ok = true;
  listCadLotChipKeys(features).forEach(({ id, status, label }) => {
    if (!replace) {
      try {
        if (map.hasImage?.(id)) return;
      } catch {
        return;
      }
    }
    const image = createCadLotChipStyleImage(getCadStockColor(status), label);
    if (!image) {
      ok = false;
      return;
    }
    if (!upsertCadLotChipImage(map, id, image, replace)) ok = false;
  });
  return ok;
}

function scheduleCadLotChipFontRefresh(map, features) {
  if (typeof document === 'undefined' || !document.fonts?.ready) return;
  if (document.fonts.status === 'loaded') return;
  if (fontRefreshPending.has(map)) return;
  fontRefreshPending.add(map);
  document.fonts.ready.then(() => {
    fontRefreshPending.delete(map);
    try {
      if (!map?.getStyle?.()) return;
    } catch {
      return;
    }
    paintCadLotChipImages(map, features, true);
  }).catch(() => {
    fontRefreshPending.delete(map);
  });
}

/**
 * @param {import('maplibre-gl').Map|null} map
 * @param {object[]} [features]
 * @returns {boolean}
 */
export function ensureCadLotChipImages(map, features = []) {
  if (!map || typeof map.addImage !== 'function' || typeof document === 'undefined') {
    return false;
  }
  const ok = paintCadLotChipImages(map, features, false);
  scheduleCadLotChipFontRefresh(map, features);
  return ok;
}
